import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  Alert,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import QRCode from 'react-qrcode-logo';
import DownloadIcon from '@mui/icons-material/Download';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import useIsMobile from '../hooks/useIsMobile';

const QRGeneratorPage: React.FC = () => {
  const [text, setText] = useState('');
  const [qrValue, setQrValue] = useState('');
  const [error, setError] = useState('');
  const isMobile = useIsMobile(768);
  const qrRef = React.useRef<HTMLDivElement>(null);

  // Generuj kod QR dynamicznie przy zmianie tekstu
  useEffect(() => {
    if (text.trim()) {
      setError('');
      setQrValue(text.trim());
    } else {
      setQrValue('');
    }
  }, [text]);

  const handleDownload = () => {
    if (!qrValue) return;

    const qrCanvas = qrRef.current?.querySelector('canvas');
    if (!qrCanvas) return;

    // Utwórz nowy canvas, który będzie zawierał logo i kod QR
    const outputCanvas = document.createElement('canvas');
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) return;

    const qrSize = qrCanvas.width;
    const quietZone = 10; // Quiet zone z komponentu QRCode
    const qrActualSize = qrSize - (quietZone * 2); // Rzeczywista szerokość czarnego kodu QR (bez białych marginesów)
    const padding = 20; // Padding wokół (góra i dół)
    const spacing = 15; // Odstęp między logo a QR code
    const borderRadius = 0; // Bez zaokrąglenia

    // Narysuj logo UNITRESCO na górze - skalowane do szerokości czarnego kodu QR
    // Najpierw obliczamy rozmiar czcionki, aby wiedzieć jaką wysokość zajmie logo
    const tempCtx = document.createElement('canvas').getContext('2d');
    if (!tempCtx) return;
    tempCtx.font = "600 18px 'Roboto', sans-serif";
    const logoText = 'UNITRESCO';
    const testTextMetrics = tempCtx.measureText(logoText);
    const testFontSize = 18;
    const targetWidth = qrActualSize;
    const fontSize = Math.floor((testFontSize * targetWidth) / testTextMetrics.width);
    tempCtx.font = `600 ${fontSize}px 'Roboto', sans-serif`;
    const finalTextMetrics = tempCtx.measureText(logoText);
    const logoHeight = finalTextMetrics.actualBoundingBoxAscent + finalTextMetrics.actualBoundingBoxDescent;

    // Ustaw rozmiar canvas (prostokąt: logo + QR code)
    // Margines od góry = padding, margines od dołu = padding (równe)
    outputCanvas.width = qrSize + (padding * 2);
    outputCanvas.height = padding + logoHeight + spacing + qrSize + padding;

    // Rysuj białe tło z zaokrąglonymi prawędziami
    ctx.fillStyle = '#FFFFFF';
    if (borderRadius > 0) {
      // Rysuj zaokrąglony prostokąt
      ctx.beginPath();
      ctx.moveTo(borderRadius, 0);
      ctx.lineTo(outputCanvas.width - borderRadius, 0);
      ctx.quadraticCurveTo(outputCanvas.width, 0, outputCanvas.width, borderRadius);
      ctx.lineTo(outputCanvas.width, outputCanvas.height - borderRadius);
      ctx.quadraticCurveTo(outputCanvas.width, outputCanvas.height, outputCanvas.width - borderRadius, outputCanvas.height);
      ctx.lineTo(borderRadius, outputCanvas.height);
      ctx.quadraticCurveTo(0, outputCanvas.height, 0, outputCanvas.height - borderRadius);
      ctx.lineTo(0, borderRadius);
      ctx.quadraticCurveTo(0, 0, borderRadius, 0);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    }

    // Narysuj logo UNITRESCO na górze - skalowane do szerokości czarnego kodu QR
    ctx.fillStyle = '#c53030';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = `600 ${fontSize}px 'Roboto', sans-serif`;
    const logoX = outputCanvas.width / 2;
    const logoY = padding;
    ctx.fillText(logoText, logoX, logoY);

    // Narysuj kod QR poniżej logo
    const qrX = padding;
    const qrY = padding + logoHeight + spacing;
    ctx.drawImage(qrCanvas, qrX, qrY);

    // Pobierz jako PNG
    outputCanvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-code-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    });
  };

  const generateSTL = () => {
    if (!qrValue) return;

    const qrCanvas = qrRef.current?.querySelector('canvas');
    if (!qrCanvas) return;

    // Utwórz canvas z logo i QR (tak jak w handleDownload)
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    const qrSize = qrCanvas.width;
    const quietZone = 10;
    const qrActualSize = qrSize - (quietZone * 2);
    const padding = 20;
    const spacing = 15;
    const borderRadius = 0; // Bez zaokrąglenia

    // Oblicz rozmiar logo (tak jak w handleDownload)
    tempCtx.font = "600 18px 'Roboto', sans-serif";
    const logoText = 'UNITRESCO';
    const testTextMetrics = tempCtx.measureText(logoText);
    const testFontSize = 18;
    const targetWidth = qrActualSize;
    const fontSize = Math.floor((testFontSize * targetWidth) / testTextMetrics.width);
    tempCtx.font = `600 ${fontSize}px 'Roboto', sans-serif`;
    const finalTextMetrics = tempCtx.measureText(logoText);
    // Używamy oryginalnej wysokości logo (bez dodatkowego marginesu) aby zachować wymiary podstawy
    const logoHeight = finalTextMetrics.actualBoundingBoxAscent + finalTextMetrics.actualBoundingBoxDescent;

    // Dodatkowy margines od góry dla pełnego renderowania tekstu (uwzględniając fontBoundingBox)
    const topMargin = Math.max(finalTextMetrics.fontBoundingBoxAscent - finalTextMetrics.actualBoundingBoxAscent, 4);
    
    // Obliczamy całkowitą wysokość zawartości (logo + spacing + QR)
    const contentHeight = logoHeight + spacing + qrSize;
    
    // Obliczamy całkowitą wysokość canvas - zmniejszamy o topMargin od góry, aby margines górny był równy dolnemu
    const totalCanvasHeight = padding + logoHeight + spacing + qrSize + padding - topMargin;
    
    // Wyśrodkowujemy zawartość w pionie - równy padding górny i dolny
    const verticalPadding = (totalCanvasHeight - contentHeight) / 2;
    const topPadding = verticalPadding;
    const bottomPadding = verticalPadding;

    // Utwórz pełny obraz (logo + QR) w wysokiej rozdzielczości dla dobrej jakości STL
    const highResScale = 2; // Wyższa rozdzielczość dla lepszej jakości
    tempCanvas.width = (qrSize + (padding * 2)) * highResScale;
    // Wysokość canvas pozostaje taka sama: topPadding + logoHeight + spacing + qrSize + bottomPadding = padding + logoHeight + spacing + qrSize + padding
    tempCanvas.height = totalCanvasHeight * highResScale;
    tempCtx.scale(highResScale, highResScale);

    // Rysuj białe tło
    tempCtx.fillStyle = '#FFFFFF';
    if (borderRadius > 0) {
      tempCtx.beginPath();
      tempCtx.moveTo(borderRadius, 0);
      tempCtx.lineTo(tempCanvas.width / highResScale - borderRadius, 0);
      tempCtx.quadraticCurveTo(tempCanvas.width / highResScale, 0, tempCanvas.width / highResScale, borderRadius);
      tempCtx.lineTo(tempCanvas.width / highResScale, tempCanvas.height / highResScale - borderRadius);
      tempCtx.quadraticCurveTo(tempCanvas.width / highResScale, tempCanvas.height / highResScale, tempCanvas.width / highResScale - borderRadius, tempCanvas.height / highResScale);
      tempCtx.lineTo(borderRadius, tempCanvas.height / highResScale);
      tempCtx.quadraticCurveTo(0, tempCanvas.height / highResScale, 0, tempCanvas.height / highResScale - borderRadius);
      tempCtx.lineTo(0, borderRadius);
      tempCtx.quadraticCurveTo(0, 0, borderRadius, 0);
      tempCtx.closePath();
      tempCtx.fill();
    } else {
      tempCtx.fillRect(0, 0, tempCanvas.width / highResScale, tempCanvas.height / highResScale);
    }

    // Rysuj logo - używamy 'alphabetic' baseline aby zapewnić pełne renderowanie wszystkich części liter
    tempCtx.fillStyle = '#c53030';
    tempCtx.textAlign = 'center';
    tempCtx.textBaseline = 'alphabetic'; // Zmienione z 'top' na 'alphabetic' dla pełnego renderowania
    tempCtx.font = `600 ${fontSize}px 'Roboto', sans-serif`;
    // Pozycja Y: topPadding + topMargin + fontBoundingBoxAscent, aby logo było wyśrodkowane i nie obcięte od góry
    // topMargin zapewnia miejsce dla pełnego renderowania tekstu, ale nie wpływa na wyśrodkowanie całej zawartości
    const logoY = topPadding + topMargin + finalTextMetrics.fontBoundingBoxAscent;
    tempCtx.fillText(logoText, tempCanvas.width / highResScale / 2, logoY);

    // Rysuj QR code - pozycjonowany względem logo (z topMargin dla logo)
    tempCtx.drawImage(qrCanvas, padding, topPadding + topMargin + logoHeight + spacing);

    // Parametry dla druku 3D
    const baseHeightMM = 1.0; // wysokość białej podstawy (1mm)
    const qrHeightMM = 0.4; // wysokość QR code (wypukłe nad podstawą - 0.4mm)
    const logoHeightMM = 0.4; // wysokość logo (wypukłe nad podstawą - 0.4mm, taka sama jak QR)
    
    // Skalowanie: zakładamy że płytka ma 40mm szerokości (QR code)
    const targetPlateWidthMM = 40.0;
    const scaleFactor = targetPlateWidthMM / (qrSize + (padding * 2));
    const pixelSizeMM = scaleFactor / highResScale; // rozmiar piksela w mm

    // Pobierz dane obrazu
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const data = imageData.data;
    const width = tempCanvas.width;
    const height = tempCanvas.height;

    let stlContent = '';
    
    // Funkcje dodające trójkąty do odpowiednich sekcji STL
    const plateTriangles: string[] = [];
    const qrTriangles: string[] = [];
    const logoTriangles: string[] = [];

    const addTriangle = (v1: number[], v2: number[], v3: number[], normal: number[], target: string[]) => {
      const triangle = `  facet normal ${normal[0].toFixed(6)} ${normal[1].toFixed(6)} ${normal[2].toFixed(6)}\n` +
        `    outer loop\n` +
        `      vertex ${v1[0].toFixed(6)} ${v1[1].toFixed(6)} ${v1[2].toFixed(6)}\n` +
        `      vertex ${v2[0].toFixed(6)} ${v2[1].toFixed(6)} ${v2[2].toFixed(6)}\n` +
        `      vertex ${v3[0].toFixed(6)} ${v3[1].toFixed(6)} ${v3[2].toFixed(6)}\n` +
        `    endloop\n` +
        `  endfacet\n`;
      target.push(triangle);
    };

    // Generuj podstawę (biała płytka)
    const plateWidthMM = (qrSize + (padding * 2)) * scaleFactor;
    const plateHeightMM = (padding + logoHeight + spacing + qrSize + padding) * scaleFactor;
    const borderRadiusMM = borderRadius * scaleFactor;

    // Funkcja pomocnicza do dodawania trójkątów podstawy
    const addPlateTriangle = (v1: number[], v2: number[], v3: number[], normal: number[]) => {
      addTriangle(v1, v2, v3, normal, plateTriangles);
    };

    // Dolna powierzchnia podstawy (biała płytka)
    if (borderRadiusMM > 0) {
      // Uproszczona wersja - prostokąt z zaokrąglonymi rogami
      // Musimy zmodyfikować addRoundedRectBase, aby używała addPlateTriangle
      // Na razie użyjemy prostego prostokąta
    }
    // Prosty prostokąt - biała płytka
    addPlateTriangle([0, 0, 0], [plateWidthMM, 0, 0], [plateWidthMM, plateHeightMM, 0], [0, 0, -1]);
    addPlateTriangle([0, 0, 0], [plateWidthMM, plateHeightMM, 0], [0, plateHeightMM, 0], [0, 0, -1]);
    
    // Boki podstawy
    addPlateTriangle([0, 0, 0], [0, 0, baseHeightMM], [plateWidthMM, 0, baseHeightMM], [0, -1, 0]);
    addPlateTriangle([0, 0, 0], [plateWidthMM, 0, baseHeightMM], [plateWidthMM, 0, 0], [0, -1, 0]);
    addPlateTriangle([plateWidthMM, 0, 0], [plateWidthMM, 0, baseHeightMM], [plateWidthMM, plateHeightMM, baseHeightMM], [1, 0, 0]);
    addPlateTriangle([plateWidthMM, 0, 0], [plateWidthMM, plateHeightMM, baseHeightMM], [plateWidthMM, plateHeightMM, 0], [1, 0, 0]);
    addPlateTriangle([plateWidthMM, plateHeightMM, 0], [plateWidthMM, plateHeightMM, baseHeightMM], [0, plateHeightMM, baseHeightMM], [0, 1, 0]);
    addPlateTriangle([plateWidthMM, plateHeightMM, 0], [0, plateHeightMM, baseHeightMM], [0, plateHeightMM, 0], [0, 1, 0]);
    addPlateTriangle([0, plateHeightMM, 0], [0, plateHeightMM, baseHeightMM], [0, 0, baseHeightMM], [-1, 0, 0]);
    addPlateTriangle([0, plateHeightMM, 0], [0, 0, baseHeightMM], [0, 0, 0], [-1, 0, 0]);
    
    // Górna powierzchnia podstawy
    addPlateTriangle([0, 0, baseHeightMM], [plateWidthMM, plateHeightMM, baseHeightMM], [plateWidthMM, 0, baseHeightMM], [0, 0, 1]);
    addPlateTriangle([0, 0, baseHeightMM], [0, plateHeightMM, baseHeightMM], [plateWidthMM, plateHeightMM, baseHeightMM], [0, 0, 1]);

    // Generuj logo i QR code jako wypukłe nad podstawą
    const extrudeZ = baseHeightMM; // poziom podstawy (zaczynamy od podstawy)

    // Przetwarzaj obraz z odpowiednim krokiem próbkowania dla dobrej jakości
    // Mniejszy krok = więcej szczegółów = lepsza jakość
    const sampleStep = 2; // Optymalny krok dla dobrej jakości i rozsądnego rozmiaru pliku
    const sampledPixelSize = pixelSizeMM * sampleStep;

    // Oblicz granice logo i QR w pikselach (na canvas)
    // Logo używa alphabetic baseline, więc zaczyna się od logoY - fontBoundingBoxAscent
    const logoStartY = Math.max(0, (logoY - finalTextMetrics.fontBoundingBoxAscent) * highResScale);
    const logoEndY = (logoY + finalTextMetrics.fontBoundingBoxDescent) * highResScale;
    const qrStartY = (topPadding + topMargin + logoHeight + spacing) * highResScale;
    const qrEndY = (topPadding + topMargin + logoHeight + spacing + qrSize) * highResScale;

    // Funkcja pomocnicza do generowania bloku wypukłości z określoną wysokością
    const addExtrudedBlock = (px: number, py: number, pz: number, blockSize: number, blockHeight: number, targetTriangles: string[]) => {
      const topZ = pz + blockHeight; // górna powierzchnia wypukłości
      
      // Górna powierzchnia wypukłości
      addTriangle([px, py, topZ], [px + blockSize, py, topZ], [px + blockSize, py + blockSize, topZ], [0, 0, 1], targetTriangles);
      addTriangle([px, py, topZ], [px + blockSize, py + blockSize, topZ], [px, py + blockSize, topZ], [0, 0, 1], targetTriangles);

      // Boki wypukłości
      addTriangle([px, py, pz], [px, py + blockSize, topZ], [px, py + blockSize, pz], [-1, 0, 0], targetTriangles);
      addTriangle([px, py, pz], [px, py, topZ], [px, py + blockSize, topZ], [-1, 0, 0], targetTriangles);
      
      addTriangle([px + blockSize, py, pz], [px + blockSize, py + blockSize, pz], [px + blockSize, py + blockSize, topZ], [1, 0, 0], targetTriangles);
      addTriangle([px + blockSize, py, pz], [px + blockSize, py + blockSize, topZ], [px + blockSize, py, topZ], [1, 0, 0], targetTriangles);
      
      addTriangle([px, py, pz], [px + blockSize, py, topZ], [px, py, topZ], [0, -1, 0], targetTriangles);
      addTriangle([px, py, pz], [px + blockSize, py, pz], [px + blockSize, py, topZ], [0, -1, 0], targetTriangles);
      
      addTriangle([px, py + blockSize, pz], [px, py + blockSize, topZ], [px + blockSize, py + blockSize, topZ], [0, 1, 0], targetTriangles);
      addTriangle([px, py + blockSize, pz], [px + blockSize, py + blockSize, topZ], [px + blockSize, py + blockSize, pz], [0, 1, 0], targetTriangles);
    };

    for (let y = 0; y < height; y += sampleStep) {
      for (let x = 0; x < width; x += sampleStep) {
        // Sprawdź kolor piksela w tym obszarze
        let isLogoPixel = false;
        let isQRPixel = false;
        
        // Sprawdź czy jesteśmy w obszarze logo lub QR (dla lepszej wydajności)
        const isInLogoArea = y >= logoStartY && y < logoEndY;
        const isInQRArea = y >= qrStartY && y < qrEndY;
        
        if (!isInLogoArea && !isInQRArea) continue;
        
        for (let dy = 0; dy < sampleStep && y + dy < height; dy++) {
          for (let dx = 0; dx < sampleStep && x + dx < width; dx++) {
            const idx = ((y + dy) * width + (x + dx)) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const alpha = data[idx + 3];
            
            if (alpha > 128) {
              const avg = (r + g + b) / 3;
              
              // Najpierw sprawdź czy to logo (czerwone #c53030 = rgb(197, 48, 48))
              // Jeśli jesteśmy w obszarze logo i piksel nie jest biały, to traktuj jako logo
              if (isInLogoArea) {
                // Logo: nie jest białe (avg < 250) - wszystko co nie jest białe w obszarze logo to logo
                if (avg < 250) {
                  isLogoPixel = true;
                  break; // Jeśli znaleźliśmy logo, nie sprawdzaj dalej
                }
              }
              
              // Sprawdź czy to QR (czarne) - tylko jeśli nie jesteśmy w obszarze logo i nie znaleźliśmy logo
              if (!isLogoPixel && isInQRArea && !isInLogoArea) {
                // QR: czarne piksele (wszystkie wartości RGB niskie)
                if (r < 100 && g < 100 && b < 100 && avg < 80) {
                  isQRPixel = true;
                }
              }
            }
          }
          if (isLogoPixel) break; // Przerwij zewnętrzną pętlę jeśli znaleźliśmy logo
        }

        if (isLogoPixel || isQRPixel) {
          // Obrót o 180° wokół osi pionowej (Y): odwracamy X, Y pozostaje bez zmian
          // Odwracamy Y, żeby logo było na górze (w canvas Y=0 to góra, w STL chcemy logo na górze)
          const originalX = x * pixelSizeMM;
          const originalY = y * pixelSizeMM;
          const px = originalX; // Bez odwrócenia X
          let py = plateHeightMM - originalY - sampledPixelSize; // Odwrócenie Y, żeby logo było na górze
          
          // Logo jest już przesunięte w canvas, więc nie trzeba dodatkowo przesuwać w STL
          // (logoTopOffset jest już uwzględnione w pozycji logo w canvas)
          
          const blockSize = sampledPixelSize;
          const pz = extrudeZ;

          // Dodaj do odpowiedniej sekcji (logo lub QR) z odpowiednią wysokością
          if (isLogoPixel) {
            addExtrudedBlock(px, py, pz, blockSize, logoHeightMM, logoTriangles);
          } else if (isQRPixel) {
            addExtrudedBlock(px, py, pz, blockSize, qrHeightMM, qrTriangles);
          }
        }
      }
    }

    // Połącz wszystkie sekcje w jeden plik STL z 3 obiektami
    // 1. Biała płytka (podstawa)
    stlContent += 'solid Plate_White\n';
    stlContent += plateTriangles.join('');
    stlContent += 'endsolid Plate_White\n';

    // 2. Czarne QR code
    stlContent += 'solid QR_Black\n';
    stlContent += qrTriangles.join('');
    stlContent += 'endsolid QR_Black\n';

    // 3. Czerwone logo
    stlContent += 'solid Logo_Red\n';
    stlContent += logoTriangles.join('');
    stlContent += 'endsolid Logo_Red\n';

    // Pobierz jeden plik STL z 3 obiektami
    const blob = new Blob([stlContent], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qr-code-3d-${Date.now()}.stl`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="md">
        {/* Logo UNITRESCO */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: '18px',
              lineHeight: '21px',
              fontWeight: 600,
              color: '#c53030',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            UNITRESCO
          </Typography>
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: isMobile ? '20px' : '24px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            mb: 3,
          }}
        >
          Generator kodów QR
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: '#fff',
            border: '1px solid #EDEDED',
            borderRadius: '4px',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Wprowadź tekst lub URL"
              placeholder="np. https://example.com lub dowolny tekst"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setError('');
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#EDEDED',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3F3FDC',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3F3FDC',
                  },
                },
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>

          {qrValue && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 4,
              }}
            >
              {/* Logo UNITRESCO nad kodem QR */}
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: '18px',
                    lineHeight: '21px',
                    fontWeight: 600,
                    color: '#c53030',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    margin: 0,
                  }}
                >
                  UNITRESCO
                </Typography>
              </Box>
              
              <Box
                ref={qrRef}
                sx={{
                  p: 2,
                  bgcolor: '#fff',
                  border: '1px solid #EDEDED',
                  borderRadius: '4px',
                  display: 'inline-block',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <QRCode
                  value={qrValue}
                  size={isMobile ? 200 : 256}
                  ecLevel="H"
                  quietZone={10}
                  qrStyle="squares"
                  eyeRadius={0}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                  }}
                >
                  <Tooltip title="Pobierz kod QR (PNG)">
                    <IconButton
                      onClick={handleDownload}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          bgcolor: '#fff',
                        },
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Pobierz STL (druk 3D)">
                    <IconButton
                      onClick={generateSTL}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          bgcolor: '#fff',
                        },
                      }}
                    >
                      <ThreeDRotationIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Typography
                sx={{
                  mt: 2,
                  color: '#666',
                  fontSize: '14px',
                  wordBreak: 'break-all',
                  textAlign: 'center',
                  maxWidth: '100%',
                }}
              >
                {qrValue}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default QRGeneratorPage;
